import { Button, Root, Progress } from './styles';
import {
  Center,
  Content,
  Nav,
  SectionController,
  SectionSummary,
} from './Components';
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { useHistory } from 'react-router-dom';
import {
  SECTIONS,
  SECTION_FILTER,
  SECTION_IDS,
  MEASURE_IDS,
  INTRO_SECTION_TEXT,
  getSection,
  FINAL_REPORT_SECTIONS,
  INTERIM_REPORT_SECTIONS,
  getDeepCloneSections,
} from './constants';
import { cloneDeep, findIndex, last, uniqBy } from 'lodash';
import {
  useReportAnswers,
  useInvestmentAnswers,
  useNextQuestion,
  useAllServiceCollections,
  useAllReportBeneficiariesFromReportWizard,
  useAllIndicatorsMin,
  useDeleteNpoReportAnswer,
  useAllReportBeneficiaries,
} from 'shared/apollo';
import { useAllQuestions, useQuestionsBySection } from './useQuestions';
import { useUser } from 'shared/auth';
import {
  //getSectionsFromLocalStorage,
  //setSectionsInLocalStorage,
  validate,
} from './utils';
import ReportSubmitted from './Components/Review/ReportSubmitted';
import DashboardStart from './Components/DashboardStart';
import BbPageHeader from 'shared/components/BbPageHeader';
import { setReturnPath } from 'shared/auth/returnPath';
import ReportRender, {
  mapFeedbackAnswers,
} from './Components/Review/ReportRender';
import Intro from 'shared/components/WelcomeBack/Intro';
import {
  isCapacityDevelopmentEnabled,
  isImpactSummaryAndDetailsValid,
} from './Components/ImpactIndicator/utils';
import PeekAnswers from './Components/PeekAnswers';
import { FINAL_BUDGET_QUESTION_ID } from './Components/Budget/utils';
import {
  getDebugNavigationQuestionId,
  getNavigationQuestionId,
} from './navigationUtils';
import { applyNavOptionValidity } from './navUtils';
import TrueImpactDebug from './TrueImpactDebug';
import { useQueryString } from 'shared/auth/hooks/useQueryString';
import { useCustomQuestions } from 'admin/pages/ManageAccounts/CustomQuestions/hook';
import {
  isReportFinalized,
  isReportInterim,
} from './Components/ReportFinalize/utils';
import AnswersModal from './Components/AnswersModal';
import { useIndicator } from 'shared/providers/IndicatorProvider';
import { checkSectionAnswers } from './answersUtils';
import CNSupportFooter from './CNSupportFooter';
import { useReport } from 'shared/providers/ReportProvider';
import { pullReportData, useTrackEvent } from 'shared/utils/useTrackEvent';
import { useSectionTracking } from './useSectionTracking';
import { useActivityPulse } from 'shared/utils/useActivityPulse';
import { isBbUserView, isCnUserView } from 'shared/utils/partnerUser';
import { AnswersProvider } from '../BetaFeedback/AnswersProvider';
import ProgramProvider from 'shared/providers/ProgramProvider';

const showSectionSummaryFirst = ['impact_summary', 'indicator_details'];

const SECTION_FIRST_QUESTION_ID = {
  dates: 10001,
  budget: 80001,
  impact_summary: 40000,
};

const getReportInProgress = (
  stage,
  status,
  currentOption,
  reportingType,
  optedInFullReporting,
) => {
  if (
    optedInFullReporting &&
    (reportingType === 'LIGHT' || reportingType === 'SUPER_LIGHT')
  ) {
    return true;
  }
  if (isReportInterim(stage)) {
    return status === 'Draft' || currentOption === 'allocation';
  }
  return isReportFinalized(stage)
    ? status === 'Draft'
    : status === 'Draft' || currentOption === 'allocation';
};

//const storedSections = getSectionsFromLocalStorage() || {};

/*const getStoredOption = (reportId, sectionId) => {
  const optionId = storedSections[reportId]?.[sectionId];
  if (optionId !== undefined) {
    storedSections[reportId][sectionId] = undefined;
  }
  return optionId;
}*/

/*const setStoredOption = (reportId, sectionId, optionId) => {
  if (storedSections[reportId] === undefined) {
    storedSections[reportId] = {
      [sectionId]: optionId
    }
  } else {
    storedSections[reportId][sectionId] = optionId
  }
  setSectionsInLocalStorage(storedSections);
}*/

/*
  Base screen/template for the report builder module
  url parameters:
  reportId = Used to pull report data and answers
  reportName = Used to show roport name on sidebar while loading data
*/

const ReportTemplate = ({
  match: {
    params: { reportId, reportName },
  },
}) => (
  <AnswersProvider reportId={reportId}>
    <ReportBuilderInner reportId={reportId} reportName={reportName} />
  </AnswersProvider>
);

export const ReportBuilderInner = ({
  reportId,
  investmentId,
  accountName,
  reportName,
  sectionName,
  programName,
  donationTotal,
  onCompleteSection,
  onQuestionChange = () => { /* noop */ },
  loadTabQuestion,
  closeAllocationPopup = () => { /* noop */ },
  onNewAnswer = () => { /* noop */ },
}) => {
  const { impactSummary, impactDetails, loadReportIndicators } = useIndicator();
  const {
    report,
    handleReloadReport: reloadReport,
    isReportEndDatePassed: isPassed,
  } = useReport();
  const reportData = report.data || {};

  const loadingData =
    report.type === 'REQUEST_INIT' || report.type === 'REQUEST_START';
  const [openModalAnswers, setOpenModalAnswers] = useState(false);

  const { customQuestions } = useCustomQuestions({
    reportId,
  });

  const [isDebug, setIsDebug] = useState(
    Boolean(useQueryString().get('debug')),
  );
  const { push } = useHistory();
  const {
    run: getNextQuestion,
    isLoading: loadingNextQuestion,
  } = useNextQuestion();

  const profolioData = reportData?.allocations?.[0]?.investment?.portfolio;
  const portfolioId = profolioData?.dbId;
  const donorId = profolioData?.accountId;

  const { data: serviceCollections } = useAllServiceCollections({
    reportId: reportData?.dbId,
    portfolioId,
    donorId,
  });
  const {
    data: beneficiaries,
    loading: beneficiariesLoading,
    error: beneficiariesError,
    refetch: beneficiariesRefetch,
  } = useAllReportBeneficiaries(reportId);
  const { data: beneficiaryTags } = useAllReportBeneficiariesFromReportWizard();
  const user = useUser();

  // Check if user is logged in with CN Auth0 or BB
  const isBbPartner = isBbUserView();
  const isCnAuth0User = isCnUserView() && !isBbPartner;
  const isThirdParty = reportData?.cnOptedIn || isBbPartner;

  // Get userId from Cognito user OR CN Auth0 localStorage
  let userId = user?.authUser?.attributes?.['custom:user_id'];

  // If no Cognito userId, check for CN Auth0 user data in localStorage
  if (!userId) {
    const cnToken = localStorage.getItem('cn_ti_token');
    if (cnToken) {
      const cnUserDataStr = localStorage.getItem('cn_user_data');
      if (cnUserDataStr) {
        try {
          const cnUserData = JSON.parse(cnUserDataStr);
          userId = cnUserData.userId;
        } catch (e) {
          console.error('Failed to parse CN user data:', e);
        }
      }
    }
  }

  const { run: runDeleteAnswer } = useDeleteNpoReportAnswer();

  useEffect(() => {
    if (!reportId) return;
    loadReportIndicators(reportId, true);
  }, [reportId]);

  /* Questions are not changing, for optimization I moved questions query outide of section, as
    it was making a query every time you change a section, leading to higher load times, UI visibility
    issue during loading, etc. Now all question are pulled and cached from the root.
  */
  const { data } = sectionName
    ? useQuestionsBySection(sectionName, investmentId) // eslint-disable-line
    : useAllQuestions(reportId, userId); // eslint-disable-line
  /* this hook pulls users answers on current report ID */

  // TODO: false && should be removed once updateNpoAnswer can work WITHOUT reportId parameter
  const {
    data: reportAnswers,
    refetch: reloadAnswers,
    loading: loadingAnswers,
    reportStatus,
  } =
    // eslint-disable-next-line no-constant-condition
    false && sectionName
      ? useInvestmentAnswers(investmentId) // eslint-disable-line
      : useReportAnswers(reportId); // eslint-disable-line
  const isCNTheme = isCnUserView() && !isBbPartner;
  const isBbTheme = isBbPartner;
  const trackEvent = useTrackEvent();
  const [showIntro, setShowIntro] = useState(
    !loadingAnswers && reportAnswers.length === 0 && !isCNTheme && !isBbTheme,
  );

  const reportEventData = useMemo(
    () =>
      reportData && reportData.stage
        ? pullReportData(
            reportData,
            isPassed,
            reportData.cnOptedIn ? 'cn-npo' : 'npo',
          )
        : {},
    [reportData, isPassed],
  );

  useEffect(() => {
    if (Object.keys(reportEventData).length === 0) return;
    trackEvent('ReportBuilder', reportEventData);
  }, [reportEventData, trackEvent]);

  useEffect(() => {
    if (loadingAnswers || isCNTheme || isBbTheme) return;
    setShowIntro(
      reportAnswers?.filter(x => x.question.section !== 'allocation')
        ?.length === 0,
    );
  }, [reportAnswers, loadingAnswers, isCNTheme, isBbTheme]);

  const handleClose = useCallback(() => {
    setShowIntro(false);
  }, [setShowIntro]);

  const [questions0, setQuestions] = useState();
  const [sections, setSections] = useState(
    isPassed
      ? FINAL_REPORT_SECTIONS()
      : isReportInterim(reportData?.stage)
      ? INTERIM_REPORT_SECTIONS()
      : cloneDeep(SECTIONS),
  );
  const focusedSection = sections.find(s => s.focus);
  const focusedOption = focusedSection?.options?.find(o => o.focus);
  const currentSection = focusedOption?.text;

  const pulsePayload = useMemo(
    () => ({ ...reportEventData, section: currentSection, userId }),
    [reportEventData, currentSection, userId],
  );

  useActivityPulse({
    eventName: 'ReportBuilderActive',
    payload: pulsePayload,
    intervalSeconds: 60,
    activeWindowMs: 60_000,
    enabled: Boolean(reportData && reportData.stage),
  });

  // Section enter/exit tracking
  useSectionTracking({
    currentSection,
    sections,
    reportEventData,
    enabled: Boolean(reportData && reportData.stage),
  });

  const [showStart, setShowStart] = useState(!sectionName);
  const [showSummary, setShowSummary] = useState(false);
  const [preloadQuestion, setPreloadQuestion] = useState();
  const [isSectionLoaded, setIsSectionLoaded] = useState();
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [baseQuestions, setBaseQuestions] = useState([]);
  const [indicators, setIndicators] = useState([]);

  const { data: indicatorsData } = useAllIndicatorsMin();

  useEffect(() => {
    if (
      indicatorsData &&
      indicatorsData.length > 0 &&
      indicators.length !== indicatorsData.length
    ) {
      setIndicators(indicatorsData);
    }
  }, [indicators, indicatorsData]);

  const updateServiceIndicators = useCallback(
    (new_indicators, services2indicators, serviceId) => {
      let indicatorFound = null;
      // Find the first matching indicator
      for (let service of services2indicators) {
        for (let indicator of service.indicators) {
          if (new_indicators.includes(indicator.name)) {
            indicatorFound = { ...indicator }; // Copy the found indicator
            break;
          }
        }
        if (indicatorFound) break;
      }
      // If an indicator was found, add it to the service
      if (indicatorFound) {
        for (let service of services2indicators) {
          if (service.id === serviceId) {
            service.indicators.push(indicatorFound);
            break;
          }
        }
      }
      return services2indicators; // Return updated array
    },
    [indicators],
  );

  const addCustomIndicator = useCallback(
    answer => {
      const serviceId = reportAnswers.filter(
        answer => answer.questionId === 40003,
      )?.[0]?.resourceId;
      const updatedIndicators = updateServiceIndicators(
        answer.text,
        indicators,
        serviceId,
      );

      setIndicators(updatedIndicators);
    },
    [reportAnswers, updateServiceIndicators, indicators],
  );

  /*
    this ref is used to call exposed methods from sectionController,
    for example to navigate to a question from outside
  */
  const sectionControllerRef = useRef();
  const questions =
    !questions0 || questions0.length === 0 ? baseQuestions : questions0;

  useEffect(() => {
    if (
      baseQuestions.length === 0 &&
      data &&
      indicators &&
      data.length &&
      indicators.length
    ) {
      const listOfIndicatorsStage6 = cloneDeep(indicators).reduce(
        (acc, service) => {
          service.indicators
            .filter(ind => ind.stageId === 6)
            .forEach(ind => {
              acc.push({ id: ind.id, name: ind.name });
            });
          return acc;
        },
        [],
      );
      const newBaseQuestions = cloneDeep(data);
      newBaseQuestions.forEach(question => {
        if (question.dbId === 40021) {
          const nextQuestionId = question.enumResponses[0].nextQuestionId;
          const enums = listOfIndicatorsStage6
            .map((ind, idx) => ({
              dbId: ind.id,
              order: 1,
              responseType: null,
              response: ind.name,
              responseValue: null,
              responseHelp: null,
              questionId: 40021,
              nextQuestionId: nextQuestionId,
              treeGroup: null,
            }))
            .sort((a, b) => a.dbId - b.dbId);
          question.enumResponses = uniqBy(enums, 'dbId');
        }
      });
      setBaseQuestions(newBaseQuestions);
    }
  }, [data, indicators, baseQuestions]);

  useEffect(() => {
    if (!sectionName || !baseQuestions?.length || isSectionLoaded) {
      return;
    }
    const firstQuestion = baseQuestions.find(
      ({ nextQuestionId }) => !nextQuestionId,
    );
    //const firstQuestion = baseQuestions.find(({ dbId }) => dbId === 20006)
    if (!firstQuestion) {
      alert(`ERROR! First question for section ${sectionName} not found!`);
      return;
    }
    setIsSectionLoaded(true);
    setPreloadQuestion(firstQuestion?.dbId);
  }, [baseQuestions, sectionName, preloadQuestion, isSectionLoaded]);
  useEffect(() => {
    setReturnPath(window.location.pathname);
    // Check if user is logged in with CN Auth0
    if (isBbPartner) {
      document.getElementById('root').classList.add('bbTheme');
    } else if (isCnUserView()) {
      document.getElementById('root').classList.add('cnTheme');
    } else {
      document.getElementById('root').classList.add('darkHeader');
    }
    return () => {
      document.getElementById('root').classList.remove('darkHeader');
      document.getElementById('root').classList.remove('cnTheme');
      document.getElementById('root').classList.remove('bbTheme');
    };
  }, []);
  /*
  useEffect(() => {
    setQuestions(baseQuestions)
  }, [baseQuestions])
*/

  /* flags that answer are loaded to avoid ui-stuttering on summary */
  useEffect(() => {
    if (!loadingAnswers) {
      setLoadingSummary(false);
    }
  }, [loadingAnswers]);

  /*
    this effect watches answers stored on db to determine if a section
    is valid by cheking if all questions tree is answered.
  */
  useEffect(() => {
    if (reportAnswers && reportAnswers.length > 0) {
      const copySection = getDeepCloneSections(sections, isPassed);
      if (copySection[0].options[0].text === INTRO_SECTION_TEXT) {
        copySection[0].options.splice(0, 1);
      }
      const isImpactModelValid = checkSectionAnswers(
        reportAnswers,
        impactSummary,
        impactDetails,
        isPassed,
        reportStatus,
        reportData?.stage,
        reportData?.year,
      );
      applyNavOptionValidity({
        sections: copySection,
        reportAnswers,
        isImpactModelValid,
        reportStage: reportData?.stage,
        sectionFilter: SECTION_FILTER,
        sectionIds: SECTION_IDS,
        getValidationKeys,
        validate,
        isCapacityDevelopmentEnabled,
      });
      setSections(copySection);
    }
  }, [reportAnswers, questions, impactSummary, impactDetails]); // eslint-disable-line

  /* Preloads a requested question on the current visible section using state preloadQuestion */
  useEffect(() => {
    if (
      questions.length !== 0 &&
      !showSummary &&
      preloadQuestion &&
      sectionControllerRef
    ) {
      sectionControllerRef.current.loadQuestion(preloadQuestion);
      setPreloadQuestion(undefined);
    }
  }, [questions, showSummary, preloadQuestion, sectionControllerRef]);

  /* Loads a requested question from allocations tabs */
  useEffect(() => {
    if (loadTabQuestion && sectionControllerRef) {
      if (!showSummary) {
        sectionControllerRef.current.loadQuestion(loadTabQuestion);
      } else {
        setShowSummary(false);
        setTimeout(() => {
          sectionControllerRef.current.loadQuestion(loadTabQuestion);
        }, 0);
      }
    }
  }, [loadTabQuestion, sectionControllerRef]); //eslint-disable-line

  /* Trigers on click on a Group: Program Start, Midpoind and End on sidebar */
  const handlePressSection = useCallback(
    sectionId => {
      if (!questions || questions.length === 0) {
        return;
      }
      const targetSection = sections[sectionId - 1];
      trackEvent('ReportBuilderSectionChanged', {
        ...reportEventData,
        section: targetSection?.options?.[0]?.text,
        source: 'section',
      });
      /*const optionId = getStoredOption(reportId, sectionId - 1);
      if (optionId !== undefined) {
        handlePressOption(sectionId - 1, optionId);
        return;
      }*/
      setShowStart(false);
      setShowSummary(false);
      const newS = cloneDeep(sections);
      newS.forEach(q => {
        q.focus = q.id === sectionId;
        if (q.options.length > 0) {
          q.options.forEach(opt => (opt.focus = false));
          if (q.id === sectionId) q.options[0].focus = true;
        }
      });
      setSections(newS);
      if (newS[sectionId - 1].options[0].valid) {
        setShowSummary(true);
      } else {
        const section = SECTION_FILTER[newS[sectionId - 1].options[0].text];
        /* Gets the next question and sets for preLoad */
        getNextQuestion({ reportId, section }, nextQuestion => {
          const updatedQuestions = cloneDeep(questions || []);
          updatedQuestions.forEach((q, idx) => {
            if (q.dbId === nextQuestion?.dbId)
              updatedQuestions[idx] = nextQuestion;
          });
          setQuestions(updatedQuestions);
          setPreloadQuestion(nextQuestion?.dbId);
        });
      }
    },
    [sections, getNextQuestion, reportId, questions, trackEvent],
  );

  /* Triggers on click on an option withing a group, eg: description, dates, etc... */
  const handlePressOption = useCallback(
    (secIdx, optIdx) => {
      setShowSummary(false);
      setShowStart(false);
      const newS = cloneDeep(sections);
      newS.forEach(s => (s.focus = false));
      newS[secIdx].focus = true;
      newS[secIdx].options.forEach(opt => (opt.focus = false));
      newS[secIdx].options[optIdx].focus = true;
      setSections(newS);
      trackEvent('ReportBuilderSectionChanged', {
        ...reportEventData,
        section: newS[secIdx].options[optIdx]?.text,
        source: 'option',
      });
      //setStoredOption(reportId, secIdx, optIdx);

      const opt = newS[secIdx].options[optIdx];
      const section = SECTION_FILTER[opt.text];

      // If user opted in to full reporting, jump to the first question of the section.
      if (
        reportData?.optedInFullReporting &&
        SECTION_FIRST_QUESTION_ID[section]
      ) {
        setPreloadQuestion(SECTION_FIRST_QUESTION_ID[section]);
        return;
      }

      if (opt.valid) {
        const sectionAnswers = (reportAnswers || [])?.filter(
          ans => ans?.question?.section === section,
        );
        const lastQuestionId =
          isDebug || showSectionSummaryFirst.includes(section)
            ? getDebugNavigationQuestionId(
                secIdx,
                opt.id,
                section,
                questions,
                sectionAnswers,
              )
            : getNavigationQuestionId(
                secIdx,
                opt.id,
                section,
                questions,
                sectionAnswers,
              );
        if (lastQuestionId) {
          setPreloadQuestion(lastQuestionId);
        } else {
          setShowSummary(true);
        }
      } else {
        // skip the intro section
        if (!section) return;
        /* Gets the next question and sets for preLoad */
        getNextQuestion({ reportId, section }, nextQuestion => {
          const updatedQuestions = cloneDeep(questions);
          updatedQuestions.forEach((q, idx) => {
            if (q.dbId === nextQuestion.dbId)
              updatedQuestions[idx] = { ...q, ...nextQuestion };
          });
          setQuestions(updatedQuestions);
          // Is is impact summary and theres no next question, is a review of the last one.
          if (section === 'impact_summary' && nextQuestion.dbId === null) {
            setPreloadQuestion(40015);
            return;
          }
          setPreloadQuestion(nextQuestion.dbId);
        });
      }
    },
    [
      sections,
      isDebug,
      getNextQuestion,
      reportId,
      questions,
      reportAnswers,
      reportData?.optedInFullReporting,
      trackEvent,
    ],
  );

  const currentOpt = useMemo(
    () =>
      sectionName
        ? { id: -1, text: sectionName, fcous: true, valid: true, error: false }
        : sections.find(s => s.focus)?.options?.find(o => o.focus),
    [sections, sectionName],
  );
  const fullWidth = true;

  const handleError = useCallback(
    (error = []) => {
      if (Array.isArray(error)) {
        const newSections = cloneDeep(sections);
        const current = newSections
          .find(s => s.focus)
          ?.options?.find(o => o.focus);
        if (current) {
          current.error = !!error.length;
          setSections(newSections);
        }
      }
    },
    [sections],
  );

  const [programNames, setProgramNames] = useState(); // eslint-disable-line
  const [programNameIndex, setProgramNameIndex] = useState(); // eslint-disable-line

  /* Summarizes answers from the user on current section  */
  const summarizeAnswers = useCallback(() => {
    let summary = [];
    const currentProgramName =
      programName ||
      reportData?.programName ||
      (programNames ? programNames[programNameIndex] : reportData.name);
    const donationTotalDisplay =
      donationTotal ?? reportData?.totalDonations ?? 0;
    const sponsorName = reportData?.sponsorName || '';
    const reportTense = reportData?.reportTense || 'will';
    const servCollKey = reportAnswers?.filter(
      ans => ans.questionId === 40002,
    )?.[0]?.text;
    const servKey = reportAnswers?.filter(ans => ans.questionId === 40003)?.[0]
      ?.text;
    const goal = (
      (serviceCollections || []).filter(coll => coll.name === servCollKey)?.[0]
        ?.services ?? []
    ).filter(serv => serv.name === servKey)?.[0]?.goal;
    const serviceGoal = goal || 'match your service goal';
    const q4Answer =
      reportAnswers?.filter(ans => ans.questionId === 40004)?.[0]?.text ||
      'your beneficiaries';
    const primaryBeneficiary = q4Answer;
    const q3answerIndicatorDetails =
      reportAnswers?.filter(ans => ans.questionId === 50003)?.[0]?.text || '0';
    const q4answerIndicatorDetails =
      reportAnswers?.filter(ans => ans.questionId === 50004)?.[0]?.text || '0';
    const q5answerIndicatorDetails =
      reportAnswers?.filter(ans => ans.questionId === 50005)?.[0]?.text || '0';
    const serviceExample = servKey;
    const socialImpactIndicator = '';
    if (reportAnswers) {
      summary = reportAnswers
        .filter(
          ans =>
            !ans?.autoAnswered &&
            ans.question.section === SECTION_FILTER[currentOpt?.text],
        )
        .reduce((acc, ans) => {
          let text = (ans?.question?.text || '')
            .replace(/<Program Name>/g, currentProgramName)
            .replace(/<Donation_total>/g, donationTotalDisplay)
            .replace(
              /<Program_name>/g,
              currentProgramName || reportData?.programName,
            )
            .replace(/<Sponsor_name>/g, sponsorName)
            .replace(/<Report_tense>/g, reportTense)
            .replace(/<Service_goal>/g, serviceGoal)
            .replace(/<Q4_answer>/g, q4Answer)
            .replace(/<Primary_beneficiary>/g, primaryBeneficiary)
            .replace(/<Q3_answer_indicator_details>/g, q3answerIndicatorDetails)
            .replace(/<Q4_answer_indicator_details>/g, q4answerIndicatorDetails)
            .replace(/<Q5_answer_indicator_details>/g, q5answerIndicatorDetails)
            .replace(/<Service_example>/g, serviceExample)
            .replace(/<Social_impact_indicator>/g, socialImpactIndicator)
            .replace(/<Report_start_date>/g, reportData?.startDate)
            .replace(/<Report_end_date>/g, reportData?.endDate)
            .replace(
              /<Primary_beneficiary_or_Intermediary>/g,
              primaryBeneficiary || 'your primary beneficiary',
            );
          // removes helper text
          if (text.includes('[HELP]')) {
            text = text.split('[HELP]')[0];
          }
          // replaces measurement code with label
          if (
            ans?.enumResponse?.responseType === 'measured' &&
            ans.text &&
            MEASURE_IDS?.[ans.text]
          ) {
            ans.text = MEASURE_IDS?.[ans.text];
          }
          const row = {
            id: ans.questionId,
            text,
            answers: [
              {
                id: ans.dbId,
                text: ans?.text ?? ans?.enumResponse?.response ?? '-',
                date1: ans?.date1,
                date2: ans?.date2,
                hiddenValidations: ans.answerValidations.reduce((acc, val) => {
                  if (val.hidden) {
                    acc.push(val.validationId);
                  }
                  return acc;
                }, []),
              },
            ],
          };
          if (ans.question.questionType !== 'INTRO') {
            acc.push(row);
          }
          return acc;
        }, []);
    }
    return summary;
  }, [
    programNames,
    programNameIndex,
    reportData,
    reportAnswers,
    currentOpt,
    donationTotal,
    serviceCollections,
  ]);

  /* get all validations on questions */
  const getValidationKeys = useCallback(() => {
    let keys = {};
    if (questions) {
      questions.forEach(({ dbId, validations }) => {
        keys[dbId] = validations;
      });
    }
    return keys;
  }, [questions]);

  /* Sets showSummary state to true to display the summary for current section */
  const handleShowSummary = skipToNextSection => {
    if (skipToNextSection) {
      handleNextOnSummary();
      return;
    }
    setTimeout(() => {
      reloadAnswers();
    }, 0);
    setLoadingSummary(true);
    setShowSummary(true);
    trackEvent('ReportBuilderSectionChanged', {
      ...reportEventData,
      section: currentSection,
      source: 'summary',
    });
    if (typeof onCompleteSection === 'function') {
      onCompleteSection();
    }
  };

  /* To be executed when an answers is saved or updated */
  const handleQuestionAnswered = () => {
    if (reloadAnswers) {
      try {
        reloadAnswers();
        setTimeout(() => {
          setLoadingSummary(false);
        }, 1000);
      } catch (warn) {
        console.warn(warn);
      }
    }
  };

  /* to be executed when edit pencil is pressed within summary */
  const handlePressEditAnswer = questionId => {
    setShowSummary(false);
    setPreloadQuestion(questionId);
  };

  /* to be executed when pressing back button on summary */
  const handleBackOnSummary = () => {
    if (investmentId) {
      closeAllocationPopup();
      return;
    }
    const lastQuestionId = last(
      (reportAnswers || []).filter(
        ans => ans.question.section === SECTION_FILTER[currentOpt?.text],
      ),
    )?.questionId;
    setShowSummary(false);
    if (lastQuestionId) setPreloadQuestion(lastQuestionId);
  };

  /* to be executed when pressing next button on summary */
  const handleNextOnSummary = () => {
    const totalSec = sections.length - 1;
    const secIdx = findIndex(sections, { focus: true });
    const optIdx = findIndex(sections[secIdx].options, { focus: true });
    const totalOpt = sections[secIdx].options.length - 1;
    if (optIdx < totalOpt) {
      return handlePressOption(secIdx, optIdx + 1);
    } else {
      if (secIdx < totalSec) {
        return handlePressSection(sections[secIdx + 1].id);
      }
    }
  };

  /*
    returns to npo reports, as builder saves on each step, it is alredy up to date
    so there is no need to save anytihng so far.
  */
  const [handleNext, setHandleNext] = useState();
  const handleSetHandleNext = useCallback(
    value => {
      setHandleNext(value);
    },
    [setHandleNext],
  );

  const handleReturn = useCallback(async () => {
    try {
      /* attemps to take actions before return */
      const currentDataFn = sectionControllerRef?.current?.getCurrentData;
      if (currentDataFn && typeof currentDataFn == 'function') {
        const currentData = currentDataFn();
        if (
          currentData?.currentQuestion.dbId === 80009 &&
          (!currentData?.crrtValue?.text?.text ||
            !currentData?.crrtValue?.text?.fileId) &&
          reportAnswers &&
          reportAnswers.length > 0
        ) {
          const answer = reportAnswers.filter(
            ans => ans.questionId === 80009,
          )?.[0];
          if (answer?.dbId) {
            await runDeleteAnswer({ id: answer.dbId });
          }
        }
      }
      if (handleNext && typeof handleNext == 'function') {
        handleNext();
      }
      const saveCrtQuestion = sectionControllerRef?.current?.triggerNext;
      if (currentOpt && typeof saveCrtQuestion == 'function') {
        saveCrtQuestion();
      }
      /* actions end */
    } catch (ex) {
      console.warn(ex);
    }
    push('/npo/reports');
  }, [
    handleNext,
    push,
    currentOpt,
    sectionControllerRef,
    reportAnswers,
    runDeleteAnswer,
  ]);

  /* runs when pression on "Your Reporting Tasks" showing the initial section */
  const handlePressStart = () => {
    trackEvent('ReportBuilderSectionChanged', {
      ...reportEventData,
      source: 'start',
    });
    setSections(s => {
      const newState = [...s];
      for (const o of newState) {
        o.focus = false;
      }
      return newState;
    });
    setShowStart(true);
  };

  /* Summarizes answers from the user on current section  */
  const questionsFiltered = useMemo(
    () =>
      (questions || []).filter(
        ({ section }) => section === SECTION_FILTER[currentOpt?.text],
      ),
    [questions, currentOpt],
  );

  /* Triggered when pressing back on first question on section */
  const handleRequestBack = useCallback(() => {
    const groupIdx = findIndex(sections, { focus: true });
    const secIdx = findIndex(sections[groupIdx]?.options, { focus: true });
    /* if sec is > 0 returns to prev sec in same group */
    if (secIdx > 0) {
      handlePressOption(groupIdx, secIdx - 1);
      /* if sec is 0 */
    } else {
      /* if group is > 0 returns to last opt of prev group */
      if (groupIdx > 0) {
        handlePressSection(sections[groupIdx - 1].id);
        /* if group is 0 returns to init */
      } else {
        setShowStart(true);
      }
    }
  }, [sections, handlePressOption, handlePressSection]);

  const handleOpenImpactSummarySection = () => {
    // Group 0, Index 3, this equals to Prgoram Start -> Impact Summary
    handlePressOption(0, getSection('impact_summary').index);
  };

  const isInterimStage = isReportInterim(reportData?.stage);
  let showSectionController = investmentId
    ? !showStart && currentOpt
    : !showStart && !showSummary && currentOpt;
  let showSectionSummary = !showStart && showSummary && currentOpt;

  const isFinalizeStage =
    reportData?.stage?.toUpperCase() === 'FINAL' &&
    reportData?.status !== 'Draft';
  if (
    isFinalizeStage ||
    isInterimStage ||
    currentOpt?.text === 'Impact Model Details' ||
    currentOpt?.text === 'Beneficiaries'
  ) {
    showSectionController = true;
    showSectionSummary = false;
  }

  const completedImpactSummary = getSection('impact_summary', sections)?.option
    .valid;
  const [isReportInprogress, setIsReportInprogress] = useState(
    getReportInProgress(
      reportData?.stage,
      reportData?.status || reportStatus,
      currentOpt?.text,
      reportData?.reportingType,
      reportData?.optedInFullReporting,
    ),
  );

  // Allow to detect the transition from Draft to Awaiting TI Feedback, to decide to show the rate popup
  const prevReportStatusRef = useRef();
  const [showRatePopup, setShowRatePopup] = useState(false);

  useEffect(() => {
    const status = reportData?.status;
    if (!status) return;
    const prev = prevReportStatusRef.current;
    if (
      prev === 'Draft' &&
      (status === 'Awaiting TI Feedback' || status === 'Published')
    ) {
      setShowRatePopup(true);
    }
    prevReportStatusRef.current = status;
  }, [reportData?.status]);

  useEffect(() => {
    setIsReportInprogress(
      getReportInProgress(
        reportData?.stage,
        reportData?.status || reportStatus,
        currentOpt?.text,
        reportData?.reportingType,
        reportData?.optedInFullReporting,
      ),
    );
    // DON'T ADD currentOpt?.text in dependencies
  }, [
    reportData?.stage,
    reportData?.status,
    reportData?.reportingType,
    reportData?.optedInFullReporting,
  ]);

  const program =
    programName ||
    reportData?.programName ||
    (programNames ? programNames[programNameIndex] : reportData?.name ?? '-');

  // TIPD-3962
  const nameOfTheProgram = isThirdParty ? reportData?.name || program : program;

  // Don't show intro screens for Charity Navigator or Blackbaud theme
  if (
    showIntro &&
    currentOpt?.text !== 'allocation' &&
    !isCNTheme &&
    !isBbTheme
  ) {
    return (
      <Intro
        reportYear={reportData?.year}
        handleClose={handleClose}
        guidanceText={undefined}
      />
    );
  }

  const loading = loadingData || loadingAnswers;

  if (isDebug) {
    window.getReport = () => reportData;
    window.getProgramName = () => reportData?.programName;
    window.getAllocations = () => reportData?.allocations;
  }

  return (
    <ProgramProvider
      programId={reportData?.programId ?? null}
      programName={programName || reportData?.programName}
    >
      <Root className="builder-root">
        <TrueImpactDebug
          isDebug={isDebug}
          handleChange={setIsDebug}
          onClick={() => setOpenModalAnswers(true)}
        />
        {openModalAnswers && (
          <AnswersModal
            report={reportData}
            open={openModalAnswers}
            answers={reportAnswers}
            isReportPassed={isPassed}
            programName={nameOfTheProgram}
            isLoading={loading}
            reloadAnswers={handleQuestionAnswered}
            handleClose={() => setOpenModalAnswers(false)}
          />
        )}
        <PeekAnswers
          report={reportData}
          program={{
            Id: reportData.programId,
            name: nameOfTheProgram,
            managerId: 0,
          }}
        />
        {!sectionName && (
          <>
            {!isCnAuth0User && !isBbTheme && (
              <Button
                className="mainSave"
                onClick={handleReturn}
                variant="outlined"
              >
                Save &amp; Return Later
              </Button>
            )}
            {!loading && (
              <Nav
                isStart={showStart}
                isThirdParty={isThirdParty}
                title={reportData?.name || reportName || ''}
                tasks={reportData?.tasks}
                subTitle="My Report"
                sections={sections}
                reportStatus={reportData?.status}
                reportStage={reportData?.stage?.toUpperCase()}
                isReportInprogress={isReportInprogress}
                handlePressSection={handlePressSection}
                handlePressOption={handlePressOption}
                handlePressStart={handlePressStart}
                isReportPassed={isPassed}
                reportId={reportId}
                onReportNameUpdate={newName => {
                  if (reportData) {
                    reportData.name = newName;
                  }
                }}
              />
            )}
          </>
        )}
        {isReportInprogress && showStart ? (
          <DashboardStart
            sections={sections}
            report={reportData}
            answers={reportAnswers}
            isThirdParty={isThirdParty}
            loadingQuestions={questions.length === 0}
            handleSectionChange={handlePressOption}
            isLoading={loading}
          />
        ) : (
          <Center
            className={isBbTheme ? 'bb-report-center' : undefined}
            padding={
              isInterimStage ||
              isFinalizeStage ||
              currentOpt?.text === 'Impact Model Details'
                ? '0'
                : undefined
            }
          >
            {isBbTheme && (
              <BbPageHeader
                title="Impact Reporting"
                subtitle="Complete your program's impact report."
              />
            )}
            <Progress display={loadingAnswers || loadingData} />
            <Content fullWidth={fullWidth}>
              {handleNext}
              {!isReportInprogress && (
                <>
                  <ReportSubmitted
                    isDebug={isDebug}
                    report={reportData}
                    answers={reportAnswers}
                    isLoading={questions.length === 0}
                    handlePressSection={handlePressSection}
                    handlePressOption={handlePressOption}
                    setIsReportInprogress={setIsReportInprogress}
                    showRatePopup={showRatePopup}
                    reloadReport={reloadReport}
                  />
                  <ReportRender
                    organizationName={accountName}
                    programName={nameOfTheProgram}
                    reportId={reportData.dbId}
                    answers={mapFeedbackAnswers(reportAnswers)}
                  />
                </>
              )}
              <SectionController
                customQuestions={customQuestions}
                questions={questionsFiltered}
                ref={sectionControllerRef}
                sectionId={currentOpt?.id}
                section={currentOpt?.text}
                isDebug={isDebug}
                isInterimStage={isInterimStage}
                isFinalizeStage={isFinalizeStage}
                onSectionEnd={handleShowSummary}
                handlePressSection={handlePressSection}
                reportId={reportId}
                report={reportData}
                programId={reportData.programId}
                investmentId={investmentId}
                compact={!!sectionName}
                isThirdParty={isThirdParty}
                programName={nameOfTheProgram}
                donationTotal={donationTotal ?? reportData?.totalDonations ?? 0}
                baseQuestions={baseQuestions}
                sponsorName={reportData?.sponsorName || ''}
                reportStartDate={reportData?.startDate}
                reportEndDate={reportData?.endDate}
                feedbackType={reportData?.feedbackType}
                answers={reportAnswers || []}
                onQuestionAnswered={handleQuestionAnswered}
                onRequestBack={handleRequestBack}
                loadingNextQuestion={loadingNextQuestion}
                onQuestionChange={onQuestionChange}
                onNewAnswer={onNewAnswer}
                onSummary={showSectionSummary}
                keepCached
                onSetHandleNext={handleSetHandleNext}
                accountName={accountName}
                serviceCollections={serviceCollections}
                beneficiaries={beneficiaries}
                beneficiariesLoading={beneficiariesLoading}
                beneficiariesError={beneficiariesError}
                beneficiariesRefetch={beneficiariesRefetch}
                beneficiaryTags={beneficiaryTags}
                indicators={indicators}
                loadingAnswers={loadingAnswers}
                reloadReport={reloadReport}
                reloadAnswers={reloadAnswers}
                completedImpactSummary={completedImpactSummary}
                handleOpenImpactSummarySection={handleOpenImpactSummarySection}
                onError={handleError}
                handlePressOption={handlePressOption}
                addCustomIndicator={addCustomIndicator}
                showSectionController={
                  isReportInprogress && showSectionController
                }
              />
              {isReportInprogress && showSectionSummary && (
                <SectionSummary
                  title={investmentId ? 'Allocation Builder' : currentOpt?.text}
                  subTitle={
                    investmentId
                      ? 'All Done'
                      : `Here are the answers you gave us for the ${currentOpt?.text} section of your report. Please take a quick look ` +
                        'and make any changes you need.'
                  }
                  questions={summarizeAnswers()}
                  onPressEditAnswer={handlePressEditAnswer}
                  reloadAnswers={handleQuestionAnswered}
                  handleBack={handleBackOnSummary}
                  handleNext={handleNextOnSummary}
                  isLoading={loadingSummary}
                  validations={getValidationKeys()}
                  investmentId={investmentId}
                  reportId={reportId}
                />
              )}
            </Content>
          </Center>
        )}
        <CNSupportFooter isThirdParty={isCnAuth0User} />
      </Root>
    </ProgramProvider>
  );
};

export default ReportTemplate;
